import React, {useRef} from 'react';
import {useTranslation} from 'react-i18next';
import {Button, List} from '@tickchak/react-share/app';
import Break from '../../../../assets/icons/Break';
import PlusThin from '../../../../assets/icons/PlusThin';
import XIcon from '../../../../assets/icons/XIcon';
import useShow from '../../../../hooks/useShow';
import useCloseOnScroll from '../../../../hooks/useCloseOnScroll';

type Placement = 'above' | 'below';
type Variant = 'brand' | 'neutral';

interface AddItemMenuProps {
    text: string;
    variant: Variant;
    placement: Placement;
    iconStart?: React.ReactNode;
    onAddLecture: () => void;
    onAddBreak: () => void;
}

const DROPDOWN_WIDTH = 240;
const DROPDOWN_GAP = 8;

function getMenuPosition(ref: React.RefObject<HTMLElement>, placement: Placement) {
    if(!ref.current) return undefined;
    const rect = ref.current.getBoundingClientRect();
    const left = rect.left + rect.width / 2 - DROPDOWN_WIDTH / 2;
    if(placement === 'above')
        return {bottom: window.innerHeight - rect.top + DROPDOWN_GAP, left};
    return {top: rect.bottom + DROPDOWN_GAP, left};
}

function AddItemMenu({text, variant, placement, iconStart, onAddLecture, onAddBreak}: AddItemMenuProps) {
    const {t} = useTranslation('timetable');
    const show = useShow();
    const triggerRef = useRef<HTMLSpanElement>(null);
    useCloseOnScroll(show.isOpen, show.close);

    const toggle = (e: React.MouseEvent) => {
        e.stopPropagation();
        show.isOpen ? show.close() : show.open();
    };

    const handleItemClick = ({id}: {id: string | number}) => {
        show.close();
        if(id === 'lecture') onAddLecture();
        else if(id === 'break') onAddBreak();
    };

    return (
        <>
            <span ref={triggerRef}>
                <Button
                    variant={variant}
                    size="chip"
                    iconStart={iconStart}
                    onClick={toggle}
                    ariaHaspopup="menu"
                    ariaExpanded={show.isOpen}
                >
                    {text}
                </Button>
            </span>
            {show.isOpen && (
                <List
                    fixedPosition={getMenuPosition(triggerRef, placement)}
                    show={show}
                    onItemClick={handleItemClick}
                    position={{width: `${DROPDOWN_WIDTH}px`, height: 'fit-content', minHeight: 'fit-content'}}
                >
                    <List.Items>
                        <List.Item.Item id="lecture" text={t('addLecture')} startIcon={<PlusThin />} />
                        <List.Item.Item id="break"   text={t('addBreak')}   startIcon={<Break />} />
                    </List.Items>
                    <List.Footer fixed>
                        <List.Footer.Close text={t('close')} iconStart={<XIcon />} size="category" />
                    </List.Footer>
                </List>
            )}
        </>
    );
}

export default AddItemMenu;
